import type { FC } from "react";
import { useEffect } from "react";
import { TIME_GRANULARITY } from "./components/consts";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import styled from "styled-components";
import { BasicDurations } from "app/services/DurationService";

import { translate } from "app/filters/i18nFilter";
import { graphGranularityToString } from "app/common/keyword-research/serp-players/consts";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { Dropdown, DropdownButton, Switcher } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import { toggleItems } from "../widget-utilities/time-granularity";
import { updateGraphGranularityValue } from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import { GraphSourceTypeView } from "app/common/keyword-research/keywords-overview/trendOverTimeTypes";
import { CategoriesDashboardFilter } from "./components/CategoriesDashboardFilter";
import { DomainsDashboardFilter } from "./components/DomainsDashboardFilter";
import { getWebsiteTypeOptions } from "app/pages/keyword-analysis/Organic";
import { getIndexOfList } from "app/common/keyword-research/clicks-over-time-graph/graphDataUtils";
import {
    CircleSwitcherItemStyled,
    DropdownButtonIconWrapper,
    SimpleDropdownItemStyled,
} from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import { GRAPH_GRANULARITIES, graphTypeTooltips } from "app/common/keyword-research/clicks-over-time-graph/graphConsts";
import { EGraphType, GRAPH_TYPES } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
const ALL_CATEGORIES_DEFAULT_ID = "All";

const getGraphDurations = (is28Days: boolean, has13MonthDuration: boolean) =>
    [
        is28Days ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : BasicDurations.LAST_MONTH,
        BasicDurations.LAST_THREE_MONTHS,
        BasicDurations.LAST_SIX_MONTHS,
        has13MonthDuration && BasicDurations.LAST_THIRTEEN_MONTHS,
    ].filter(Boolean);

const KeywordAnalysisGraphDashboardWidgetFiltersContainer = styled.div`
    .dynamicFilter.overview-performance-dynamic-filters {
        width: 100% !important;
    }
`;

const getWebsiteTypeOptionsList = () =>
    [
        {
            id: ALL_CATEGORIES_DEFAULT_ID,
            text: "topsites.table.site.functionality.filter.all.website.types",
        },
        ...getWebsiteTypeOptions(),
    ].map((item) => ({ ...item, text: translate(item.text) }));

const SourceTypeMap: Record<keyof typeof EKeywordTabs, string> = {
    Organic: "organic",
    Paid: "paid",
    Total: "all",
};

export const sourceTypes = [
    {
        id: SourceTypeMap.Total,
        text: EKeywordTabs[EKeywordTabs.Total],
    },
    {
        id: SourceTypeMap.Organic,
        text: EKeywordTabs[EKeywordTabs.Organic],
    },
    {
        id: SourceTypeMap.Paid,
        text: EKeywordTabs[EKeywordTabs.Paid],
    },
];
const graphSourceTypes = [
    {
        id: GraphSourceTypeView.TOTAL,
        text: "keyword.analysis.dashboard.trend_over_time.graph_source_type.total",
    },
    {
        id: GraphSourceTypeView.BREAKDOWN,
        text: "keyword.analysis.dashboard.trend_over_time.graph_source_type.breakdown",
    },
];
const getSelectedGraphSourceType = (graphSourceType: string) =>
    graphSourceTypes.findIndex((item) => item.id === graphSourceType);
const TITLE_KEY_TIME_GRANULARITY = "TimeGranularity";
const TITLE_KEY_DURATION = "History";
const TITLE_KEY_GRAPH_TYPE = "GraphType";
const TITLE_KEY_SOURCE_TYPE = "SourceType";
const TITLE_KEY_GRAPH_SOURCE_TYPE = "GraphSourceType";
const GRAPH_TYPE = "graphType";
const DURATION = "graphDuration";
const SOURCE_TYPE = "sourceType";
const DOMAINS = "domains";
const CATEGORY = "category";
const FUNC_FLAG = "funcFlag";
const GRAPH_SOURCE_TYPE = "graphSourceType";
const KEY = "dynamicFiltersContainer";
const DEFAULT_GRANULARITY = GranularityNames.WEEKLY;
const DEFAULT_GRAPH_TYPE = EGraphType["chart-stacked-share"] as any;
const DEFAULT_DURATION = BasicDurations.LAST_TWENTY_EIGHT_DAYS;
const DEFAULT_SOURCE_TYPE = SourceTypeMap.Organic;
const DEFAULT_GRAPH_SOURCE_TYPE = GraphSourceTypeView.TOTAL;
const METRICS_WITHOUT_GRAPH_TYPE = ["KeywordAnalysisTrendOverTime"];
const METRICS_WITHOUT_SOURCE_TYPE = ["KeywordAnalysisTrendOverTime"];
const METRICS_WITH_GRAPH_SOURCE_TYPE = ["KeywordAnalysisTrendOverTime"];
const METRICS_WITH_13_MONTH_DURATION = ["KeywordAnalysisTrendOverTime", "ClicksOverTime", "UrlClicksOverTime"];

interface IFilterObject {
    [TIME_GRANULARITY]: string;
    [GRAPH_TYPE]: number;
    [DURATION]: string;
    [SOURCE_TYPE]: string;
    [GRAPH_SOURCE_TYPE]: string;
    [DOMAINS]: string;
    [CATEGORY]: string;
    [FUNC_FLAG]: string;
}

interface IGraphDurationFilter {
    onFilterChange: any;
    graphDurations: any;
    durationSelectedId: number;
    timeGranularity: string;
}

const getGranularityName = (granularity: string) => {
    return Object.values(toggleItems).find((item) => item.title === granularity)?.value;
};

const isGraphGranularityOptionDisabled = (graphGranularityOption: string, selectedGraphDuration: string) => {
    const selectedGraphGranularityName = getGranularityName(graphGranularityOption);
    if (selectedGraphGranularityName === GranularityNames.MONTHLY) {
        return (
            selectedGraphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ||
            selectedGraphDuration === BasicDurations.LAST_MONTH
        );
    }
    if (
        selectedGraphGranularityName === GranularityNames.DAILY ||
        selectedGraphGranularityName === GranularityNames.WEEKLY
    ) {
        return selectedGraphDuration === BasicDurations.LAST_THIRTEEN_MONTHS;
    }
};

const GraphDurationFilter: FC<IGraphDurationFilter> = (props) => {
    const { onFilterChange, graphDurations, durationSelectedId, timeGranularity } = props;
    const selectedGraphGranularityName = getGranularityName(timeGranularity);

    const onItemClicked = (value) => {
        const updatedGraphGranularity = updateGraphGranularityValue(
            graphDurations[value],
            selectedGraphGranularityName,
            TIME_GRANULARITY,
        );
        if (updatedGraphGranularity) {
            const newFilters = {
                ...updatedGraphGranularity,
                [DURATION]: graphDurations[value],
            };
            onFilterChange(null, null, newFilters);
        } else {
            onFilterChange(DURATION, graphDurations[value]);
        }
    };

    return (
        <Switcher
            onItemClick={onItemClicked}
            selectedIndex={durationSelectedId === -1 ? 0 : durationSelectedId}
            customClass="CircleSwitcher"
        >
            {graphDurations.map((graphDuration) => (
                <CircleSwitcherItemStyled
                    key={graphDuration}
                    disabled={false}
                >
                    {graphDuration.toUpperCase()}
                </CircleSwitcherItemStyled>
            ))}
        </Switcher>
    );
};

export const KeywordAnalysisGraphDashboardWidgetFilters: FC<any> = (props) => {
    const {
        widgetFilters: filterObject,
        changeFilter: onFilterChangeCallback,
        widgetDuration,
        widgetMetric,
        widgetPreview,
        widgetKeys,
    } = props;
    const websiteTypeOptions = getWebsiteTypeOptionsList();
    const is28Days = widgetDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const has13MonthDuration = METRICS_WITH_13_MONTH_DURATION.includes(widgetMetric);
    const graphDurations = getGraphDurations(is28Days, has13MonthDuration);
    useEffect(() => {
        const defaultFilters = {};
        if (!filterObject[TIME_GRANULARITY]) {
            defaultFilters[TIME_GRANULARITY] = DEFAULT_GRANULARITY;
        }
        if (!filterObject[GRAPH_TYPE]) {
            defaultFilters[GRAPH_TYPE] = DEFAULT_GRAPH_TYPE;
        }
        if (!filterObject[DURATION]) {
            defaultFilters[graphDurations[DURATION]] = DEFAULT_DURATION;
        }
        if (!filterObject[SOURCE_TYPE]) {
            defaultFilters[SOURCE_TYPE] = DEFAULT_SOURCE_TYPE;
        }
        if (!filterObject[GRAPH_SOURCE_TYPE]) {
            defaultFilters[GRAPH_SOURCE_TYPE] = DEFAULT_GRAPH_SOURCE_TYPE;
        }
        onFilterChangeCallback(defaultFilters);
    }, []);

    useEffect(() => {
        const duration = filterObject.graphDuration
            ? filterObject.graphDuration
            : widgetDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS
              ? BasicDurations.LAST_TWENTY_EIGHT_DAYS
              : BasicDurations.LAST_MONTH;
        onFilterChange(DURATION, duration);
    }, [widgetDuration]);

    const onFilterChange = (
        filterName?: keyof IFilterObject,
        value?: string,
        updatedFiltersObject?: Record<string, unknown>,
    ) => {
        let newData = { ...filterObject };
        if (updatedFiltersObject) {
            newData = { ...newData, ...updatedFiltersObject };
        } else {
            newData[filterName] = value;
        }

        const updatedFilters = Object.entries(newData).map(([key, value]) => ({
            name: key,
            value,
        }));

        onFilterChangeCallback(updatedFilters);
    };

    const handleDomainsChange = (value) => {
        onFilterChange(DOMAINS, value);
    };

    const onSelectWebsiteType = (item) => {
        onFilterChange(FUNC_FLAG, item?.id === ALL_CATEGORIES_DEFAULT_ID ? undefined : item?.id);
    };

    const graphTypeId = filterObject?.graphType ?? DEFAULT_GRAPH_TYPE;
    const durationSelectedId = graphDurations.findIndex(
        (duration) => duration === (filterObject.graphDuration ?? DEFAULT_DURATION),
    );
    if (durationSelectedId === -1) {
        onFilterChange(DURATION, graphDurations[0]);
    }

    const timeGranularitySelectedId = getIndexOfList(
        graphGranularityToString,
        filterObject.timeGranularity ?? DEFAULT_GRANULARITY,
    );

    const selectedSourceTypeId = filterObject?.sourceType ?? sourceTypes[0].id;
    return (
        <KeywordAnalysisGraphDashboardWidgetFiltersContainer
            key={KEY}
            className="filters"
        >
            {!METRICS_WITHOUT_SOURCE_TYPE.includes(widgetMetric) ? (
                <div
                    key={TITLE_KEY_SOURCE_TYPE}
                    className="dynamicFilter"
                >
                    <h5>
                        <I18n>home.dashboards.wizard.filters.trafficType</I18n>
                    </h5>
                    <StatefulDropdown
                        items={sourceTypes}
                        selectedId={selectedSourceTypeId}
                        onSelect={(value) => {
                            onFilterChange(SOURCE_TYPE, value.id);
                        }}
                    />
                </div>
            ) : null}
            {!METRICS_WITHOUT_GRAPH_TYPE.includes(widgetMetric) ? (
                <div
                    key={TITLE_KEY_GRAPH_TYPE}
                    className="dynamicFilter"
                >
                    <h5>
                        <I18n>home.dashboards.wizard.filters.graphType</I18n>
                    </h5>
                    <Dropdown
                        appendTo={"body"}
                        dropdownPopupPlacement={"ontop-left"}
                        selectedIds={{ [graphTypeId]: true }}
                        onClick={(value) => {
                            onFilterChange(GRAPH_TYPE, value.id);
                        }}
                        itemsComponent={(props) => (
                            <SimpleDropdownItemStyled {...props}>
                                <SWReactIcons
                                    iconName={props.icon}
                                    size="xs"
                                />
                                {props.children}
                            </SimpleDropdownItemStyled>
                        )}
                    >
                        {[
                            <DropdownButton key="item0">
                                <DropdownButtonIconWrapper>
                                    <SWReactIcons
                                        iconName={GRAPH_TYPES[graphTypeId]}
                                        size="xs"
                                    />
                                </DropdownButtonIconWrapper>
                                {translate(graphTypeTooltips[EGraphType[graphTypeId]]?.title)}
                            </DropdownButton>,
                            ...GRAPH_TYPES.map((graphType, id) => ({
                                id,
                                text: translate(graphTypeTooltips[graphType].title),
                                icon: graphType,
                            })),
                        ]}
                    </Dropdown>
                </div>
            ) : null}

            <div
                key={TITLE_KEY_TIME_GRANULARITY}
                className="dynamicFilter"
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.granularity</I18n>
                </h5>
                <Switcher
                    onItemClick={(value) => onFilterChange(TIME_GRANULARITY, graphGranularityToString[value])}
                    selectedIndex={timeGranularitySelectedId}
                    customClass="CircleSwitcher"
                >
                    {GRAPH_GRANULARITIES.map((granularity) => (
                        <CircleSwitcherItemStyled
                            key={granularity}
                            disabled={isGraphGranularityOptionDisabled(granularity, graphDurations[durationSelectedId])}
                        >
                            {translate(granularity)}
                        </CircleSwitcherItemStyled>
                    ))}
                </Switcher>
            </div>
            <div
                key={TITLE_KEY_DURATION}
                className="dynamicFilter"
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.history</I18n>
                </h5>
                <GraphDurationFilter
                    onFilterChange={onFilterChange}
                    graphDurations={graphDurations}
                    durationSelectedId={durationSelectedId}
                    timeGranularity={GRAPH_GRANULARITIES[timeGranularitySelectedId]}
                />
            </div>
            {METRICS_WITH_GRAPH_SOURCE_TYPE.includes(widgetMetric) ? (
                <div
                    key={TITLE_KEY_GRAPH_SOURCE_TYPE}
                    className="dynamicFilter"
                >
                    <h5>
                        <I18n>home.dashboards.wizard.filters.graph.source.type</I18n>
                    </h5>
                    <Switcher
                        onItemClick={(value) => onFilterChange(GRAPH_SOURCE_TYPE, graphSourceTypes[value].id)}
                        selectedIndex={getSelectedGraphSourceType(
                            filterObject.graphSourceType ?? DEFAULT_GRAPH_SOURCE_TYPE,
                        )}
                        customClass="CircleSwitcher"
                    >
                        {graphSourceTypes.map((graphSourceType) => (
                            <CircleSwitcherItemStyled
                                key={graphSourceType.id}
                                disabled={false}
                            >
                                {translate(graphSourceType.text)}
                            </CircleSwitcherItemStyled>
                        ))}
                    </Switcher>
                </div>
            ) : null}
            <div
                key="websiteType"
                className="dynamicFilter"
            >
                <h5>
                    <I18n>home.dashboards.wizard.filters.WebsiteType</I18n>
                </h5>
                <StatefulDropdown
                    key="websiteTypeFilter"
                    items={websiteTypeOptions}
                    selectedId={filterObject?.funcFlag ?? websiteTypeOptions[0].id}
                    onSelect={onSelectWebsiteType}
                    hasSearch={false}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>keywords.analysis.filters.domains.projects.button</I18n>
                </h5>
                <DomainsDashboardFilter
                    domainsList={widgetPreview?.filters?.DomainsList}
                    domains={filterObject?.domains}
                    onSelect={(value) => {
                        handleDomainsChange(value);
                    }}
                    keyword={widgetKeys[0]?.id}
                />
            </div>
            <div className="dynamicFilter">
                <h5>
                    <I18n>home.dashboards.wizard.filters.website.industries</I18n>
                </h5>

                <CategoriesDashboardFilter
                    categories={widgetPreview?.filters?.Categories?.Category}
                    customCategories={widgetPreview?.filters?.Categories?.CustomCategory}
                    selectedCategory={filterObject?.category}
                    onSelect={(item) => {
                        onFilterChange(CATEGORY, item?.id === ALL_CATEGORIES_DEFAULT_ID ? undefined : item?.id);
                    }}
                />
            </div>
        </KeywordAnalysisGraphDashboardWidgetFiltersContainer>
    );
};
